import { registerResetFn } from '@/store/reset';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { type AppliedFilters, type RoadmapFilters, type RoadmapFilterStore } from './filter.types';

export const initialAppliedFilters: AppliedFilters = {
  platform: [],
  releases: [],
  version: [],
  types: [],
  origin: [],
  statuses: [],
  okrs: [],
  productOwners: [],
  productManagers: [],
  techManagers: [],
  industryManagers: [],
  globalProductOwner: [],
  productUnits: [],
  owner: [],
  industries: null,
  fundingSources: [],
  releaseOption: 'Specific',
  portfolios: [],
  itemStatuses: [],
  products: [],
  resourceIds: [],
  resourceGroupIds: [],
  showDevelopmentItemsOnly: false,
  categories: [],
  priorities: [],
  unitsImpacted: [],
  isRequiredUnit: false,
  userItems: [],
  searchText: '',
  externalOrigin: '',
};

function buildAppliedFilters(state: RoadmapFilterStore): AppliedFilters {
  return {
    platform: state.selectedPlatform,
    releases: state.selectedRelease,
    version: state.selectedVersion,
    types: state.selectedType,
    origin: state.selectedOrigin,
    statuses: state.selectedStatus,
    okrs: state.selectedOKRs,
    productOwners: state.selectedProductOwners,
    productManagers: state.selectedProductManagers,
    techManagers: state.selectedTechManagers,
    industryManagers: state.selectedIndustryManagers,
    globalProductOwner: state.selectedGlobalProductOwner,
    productUnits: state.selectedProductUnits,
    owner: state.selectedOwner,
    industries: state.selectedIndustries,
    fundingSources: state.selectedFundingSources,
    releaseOption: state.selectedReleaseOption,
    portfolios: state.selectedPortfolios,
    itemStatuses: state.selectedItemStatus,
    products: state.selectedProducts,
    resourceIds: state.selectedResourceIds,
    resourceGroupIds: state.selectedResourceGroupIds,
    showDevelopmentItemsOnly: state.showDevelopmentItemsOnly,
    categories: state.selectedCategories,
    priorities: state.selectedPriorities,
    unitsImpacted: state.selectedUnitsImpacted,
    isRequiredUnit: state.isRequiredUnit,
    userItems: state.userItems ?? [],
    searchText: state.searchText,
    externalOrigin: state.externalOrigin,
  };
}

const initialFilterState: RoadmapFilterStore = {
  selectedPlatform: [],
  selectedRelease: [],
  selectedVersion: [],
  selectedType: [],
  selectedOrigin: [],
  selectedStatus: [],
  selectedOKRs: [],
  selectedProductOwners: [],
  selectedProductManagers: [],
  selectedTechManagers: [],
  selectedIndustryManagers: [],
  selectedGlobalProductOwner: [],
  selectedProductUnits: [],
  selectedOwner: [],
  selectedPortfolios: [],
  selectedProducts: [],
  selectedIndustries: null,
  selectedItemStatus: [],
  selectedFundingSources: [],
  selectedReleaseOption: 'Specific',
  searchText: '',
  viewMode: 'Grid',
  year: new Date().getFullYear(),
  showBy: 'By Resource',
  userItems: [],
  selectedResourceGroupIds: [],
  selectedResourceIds: [],
  showDevelopmentItemsOnly: false,
  selectedCategories: [],
  selectedPriorities: [],
  selectedUnitsImpacted: [],
  isRequiredUnit: false,
  appliedFilters: initialAppliedFilters,
  externalOrigin: '',
  filtersApplied: false,
  filtersDirty: false,
};

/** Sets filter field(s) and marks filters as dirty (needing re-apply). */
export const setFilterField = (update: Partial<RoadmapFilterStore>) =>
  useRoadmapFilterStore.setState({ ...update, filtersDirty: true });

export const useRoadmapFilterStore = create<RoadmapFilterStore>()(
  persist(() => ({ ...initialFilterState }), {
    name: 'storage-roadmap-filter',
    version: 1,
    partialize: (state) => ({
      viewMode: state.viewMode,
      year: state.year,
      showBy: state.showBy,
    }),
  }),
);

registerResetFn(() => useRoadmapFilterStore.setState(initialFilterState));

// --- Complex actions ---

export const clearFilter = () => {
  const { viewMode, year, showBy } = useRoadmapFilterStore.getState();
  useRoadmapFilterStore.setState({
    ...initialFilterState,
    viewMode,
    year,
    showBy,
    appliedFilters: { ...initialAppliedFilters },
    filtersApplied: true,
    filtersDirty: false,
  });
};

export const applyFilters = () =>
  useRoadmapFilterStore.setState((state) => ({
    appliedFilters: buildAppliedFilters(state),
    filtersApplied: true,
    filtersDirty: false,
  }));

export const setFilters = (filters: Partial<RoadmapFilters>) => {
  const {
    platform = [],
    releases = [],
    version = [],
    types = [],
    origin = [],
    statuses = [],
    okrs = [],
    productOwners = [],
    productManagers = [],
    techManagers = [],
    industryManagers = [],
    globalProductOwner = [],
    productUnits = [],
    owner = [],
    industries = null,
    fundingSources = [],
    portfolios = [],
    itemStatuses = [],
    releaseOption = 'Specific',
    products = [],
    showDevelopmentItemsOnly = false,
    categories = [],
    priorities = [],
    unitsImpacted = [],
    isRequiredUnit = false,
    resourceIds = [],
    resourceGroupIds = [],
    userItems = [],
    externalOrigin = '',
    searchText = '',
  } = filters;

  useRoadmapFilterStore.setState((state) => {
    const newState: RoadmapFilterStore = {
      ...state,
      selectedPlatform: platform,
      selectedRelease: releases,
      selectedVersion: version,
      selectedType: types,
      selectedOrigin: origin,
      selectedStatus: statuses,
      selectedOKRs: okrs,
      selectedProductOwners: productOwners,
      selectedProductManagers: productManagers,
      selectedTechManagers: techManagers,
      selectedIndustryManagers: industryManagers,
      selectedGlobalProductOwner: globalProductOwner,
      selectedProductUnits: productUnits,
      selectedOwner: owner,
      selectedIndustries: industries,
      selectedFundingSources: fundingSources,
      selectedPortfolios: portfolios,
      selectedItemStatus: itemStatuses,
      selectedReleaseOption: releaseOption,
      selectedProducts: products,
      searchText,
      showDevelopmentItemsOnly,
      selectedCategories: categories,
      selectedPriorities: priorities,
      selectedUnitsImpacted: unitsImpacted,
      isRequiredUnit,
      selectedResourceIds: resourceIds,
      selectedResourceGroupIds: resourceGroupIds,
      userItems,
      filtersApplied: true,
      filtersDirty: false,
      appliedFilters: initialAppliedFilters,
      externalOrigin: externalOrigin,
    };
    newState.appliedFilters = buildAppliedFilters(newState);
    return newState;
  });
};

// --- Selectors ---

export const getFiltersSelector = (state: RoadmapFilterStore): RoadmapFilters => ({
  platform: state.selectedPlatform ?? [],
  releases: state.selectedRelease ?? [],
  version: state.selectedVersion ?? [],
  types: state.selectedType ?? [],
  origin: state.selectedOrigin ?? [],
  statuses: state.selectedStatus ?? [],
  okrs: state.selectedOKRs ?? [],
  productOwners: state.selectedProductOwners ?? [],
  productManagers: state.selectedProductManagers ?? [],
  techManagers: state.selectedTechManagers ?? [],
  industryManagers: state.selectedIndustryManagers ?? [],
  globalProductOwner: state.selectedGlobalProductOwner ?? [],
  productUnits: state.selectedProductUnits ?? [],
  owner: state.selectedOwner ?? [],
  industries: state.selectedIndustries ?? null,
  fundingSources: state.selectedFundingSources ?? [],
  releaseOption: state.selectedReleaseOption ?? 'Specific',
  portfolios: state.selectedPortfolios ?? [],
  itemStatuses: state.selectedItemStatus ?? [],
  products: state.selectedProducts ?? [],
  resourceGroupIds: state.selectedResourceGroupIds ?? [],
  resourceIds: state.selectedResourceIds ?? [],
  showDevelopmentItemsOnly: state.showDevelopmentItemsOnly ?? false,
  categories: state.selectedCategories ?? [],
  priorities: state.selectedPriorities ?? [],
  unitsImpacted: state.selectedUnitsImpacted ?? [],
  isRequiredUnit: state.isRequiredUnit ?? false,
  userItems: state.userItems ?? [],
  externalOrigin: state.externalOrigin ?? '',
});

export const getAppliedFiltersSelector = (state: RoadmapFilterStore): AppliedFilters =>
  state.appliedFilters ?? initialAppliedFilters;

export const getFiltersAppliedSelector = (state: RoadmapFilterStore): boolean => state.filtersApplied ?? false;

export const getFiltersDirtySelector = (state: RoadmapFilterStore): boolean => state.filtersDirty ?? false;
